import { useState, useEffect, useMemo, useCallback } from "react";
import useStickyContent from "@/hooks/useStickyContent";
import useScrollSpy from "@/hooks/useScrollSpy";
import {
  extractHeadingsFromDOM,
  smoothScrollToElement,
} from "@/utils/tableOfContents";
import * as StyledToc from "./Styled.Toc";

const TableOfContents = ({
  navbarHeight = 58,
  targetElementId = "article-content",
  className = "",
}) => {
  const [tableOfContents, setTableOfContents] = useState([]);

  // Simplified sticky hook - only handles hide logic
  const { stickyRef, shouldHide, isVisible, topOffset } = useStickyContent({
    minWidth: 900,
    topOffset: navbarHeight + 30,
    targetElementId,
    hideAtEnd: true,
  });

  const extractTOC = useCallback(() => {
    const targetElement = document.querySelector(`#${targetElementId}`);
    if (!targetElement) return [];

    const toc = extractHeadingsFromDOM(`#${targetElementId}`);
    return toc.filter(
      (item) =>
        item.level >= 2 && item.level <= 4 && !item.id?.startsWith("footnote-")
    );
  }, [targetElementId]);

  useEffect(() => {
    const targetElement = document.querySelector(`#${targetElementId}`);
    if (!targetElement) return;

    const updateTOC = () => setTableOfContents(extractTOC());

    updateTOC();

    const observer = new MutationObserver((mutations) => {
      const hasHeadingChanges = mutations.some((mutation) =>
        Array.from(mutation.addedNodes)
          .concat(Array.from(mutation.removedNodes))
          .some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.matches?.("h1, h2, h3, h4, h5, h6") ||
                node.querySelector?.("h1, h2, h3, h4, h5, h6"))
          )
      );

      if (hasHeadingChanges) updateTOC();
    });

    observer.observe(targetElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id"],
    });

    return () => observer.disconnect();
  }, [extractTOC]);

  const headingIds = useMemo(
    () => tableOfContents.map((item) => item.id),
    [tableOfContents]
  );

  const activeId = useScrollSpy(headingIds);

  if (!tableOfContents.length) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    smoothScrollToElement(id, navbarHeight + 30);
  };

  return (
    <>
      <StyledToc.TocMenu
        ref={stickyRef}
        $sticky
        $topOffset={topOffset}
        $shouldHide={!isVisible}
        className={`${className} toc-sticky ${
          isVisible ? "toc-show" : "toc-hide"
        }`}
      >
        <StyledToc.TocMenuSummary>
          Table of Contents
          <hr />
        </StyledToc.TocMenuSummary>
        <StyledToc.TocMenuInnerScroll>
          {tableOfContents.map((item, index) => (
            <StyledToc.TocMenuList key={`${item.id}-${index}`}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                title={item.title}
                className={activeId === item.id ? "active" : ""}
                style={{
                  paddingLeft: `${(item.level - 2) * 20}px`,
                  // fontWeight: activeId === item.id ? "bold" : "normal",
                }}
              >
                {item.title}
              </a>
            </StyledToc.TocMenuList>
          ))}
        </StyledToc.TocMenuInnerScroll>
      </StyledToc.TocMenu>
    </>
  );
};

export default TableOfContents;
